import React from "react";

const CALENDAR_URL =
  "https://app.startpoint.biz/v2/location/d57vIP0iwfK5hsuQlkut/calendars/view?user_ids=bH8McccR6vaPIgln0j3M";

const ArrowIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MiniCheck = () => (
  <svg className="w-4 h-4 text-fut-primary shrink-0 mt-1" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12.5l4.2 4.2L19 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Contact = ({ translations = {} }) => {
  const isSpanish = translations?.header?.home === "Inicio";

  const t = isSpanish
    ? {
        heroEyebrow: "Evaluación inicial",
        heroTitle:
          "Antes de declarar, estructurar o preparar tu negocio para capital, revisemos qué necesitas realmente.",
        heroText:
          "San Jose Obrero LLC te ayuda a ordenar tu estructura empresarial, impuestos, documentos y preparación financiera con una ruta clara antes de tomar decisiones importantes.",
        primaryCta: "Solicitar evaluación",
        secondaryCta: "Agendar llamada",
        mainCardTitle:
          "Primero entendemos tu situación. Luego definimos el siguiente paso.",
        mainCardText:
          "Este formulario permite enviar tu información antes de reservar una llamada. Así podemos revisar tu necesidad con más claridad y orientarte hacia el servicio correcto.",
        pillars: [
          {
            label: "Claridad",
            text: "Revisamos tu situación para identificar qué necesitas primero.",
          },
          {
            label: "Orden",
            text: "Organizamos el punto de partida: impuestos, estructura o documentos.",
          },
          {
            label: "Siguiente paso",
            text: "Te indicamos la ruta más lógica antes de avanzar.",
          },
        ],
        formTitle: "Solicita tu evaluación inicial",
        formText:
          "Completa estos datos. Revisamos tu situación antes de recomendar un servicio o paquete.",
        fields: {
          name: "Nombre completo",
          phone: "Teléfono",
          email: "Correo electrónico",
          service: "Servicio de interés",
          message: "Describe brevemente tu situación",
        },
        serviceOptions: [
          "Impuestos corporativos y LLC",
          "Impuestos personales y small business",
          "Estructura empresarial",
          "Perfil empresarial y preparación financiera",
          "Mapa de capital",
          "Educación financiera y revisión de perfil",
        ],
        submit: "Enviar evaluación",
        bookingTitle: "Agenda una llamada",
        bookingText:
          "Si deseas revisar tu caso con más detalle, puedes reservar un espacio disponible en el calendario.",
        widgetLabel: "Abrir calendario de citas",
        resultTitle: "Tu próximo paso con más claridad",
        resultText:
          "Envía tu información y recibe una orientación inicial para saber qué necesita tu negocio antes de actuar.",
        callLabel: "Llamar",
        whatsappLabel: "WhatsApp",
      }
    : {
        heroEyebrow: "Initial evaluation",
        heroTitle:
          "Before filing, structuring, or preparing your business for capital, let’s review what you actually need.",
        heroText:
          "San Jose Obrero LLC helps you organize your business structure, taxes, documents, and financial readiness with a clear route before making important decisions.",
        primaryCta: "Request evaluation",
        secondaryCta: "Schedule call",
        mainCardTitle:
          "First we understand your situation. Then we define the next step.",
        mainCardText:
          "This form lets you send your information before booking a call. That helps us review your need with more clarity and guide you toward the right service.",
        pillars: [
          {
            label: "Clarity",
            text: "We review your situation to identify what you need first.",
          },
          {
            label: "Order",
            text: "We organize the starting point: taxes, structure, or documents.",
          },
          {
            label: "Next step",
            text: "We show you the most logical route before moving forward.",
          },
        ],
        formTitle: "Request your initial evaluation",
        formText:
          "Complete these details. We review your situation before recommending a service or package.",
        fields: {
          name: "Full name",
          phone: "Phone",
          email: "Email",
          service: "Service of interest",
          message: "Briefly describe your situation",
        },
        serviceOptions: [
          "Corporate & LLC taxes",
          "Personal & small business taxes",
          "Business structure",
          "Business profile & financial readiness",
          "Capital map",
          "Financial education & profile review",
        ],
        submit: "Submit evaluation",
        bookingTitle: "Schedule a call",
        bookingText:
          "If you want to review your case in more detail, you can reserve an available time on the calendar.",
        widgetLabel: "Open appointment calendar",
        resultTitle: "Your next step with more clarity",
        resultText:
          "Submit your information and receive initial guidance to understand what your business needs before taking action.",
        callLabel: "Call",
        whatsappLabel: "WhatsApp",
      };

  return (
    <main className="min-h-screen bg-[#050B14] text-fut-text-light font-sans pb-20 md:pb-0 overflow-hidden">
      <section className="relative min-h-screen px-6 py-10 md:py-16 flex items-center">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(0,212,255,.26),transparent_27%),radial-gradient(circle_at_84%_28%,rgba(59,130,246,.20),transparent_30%),linear-gradient(135deg,#001A33_0%,#06111f_42%,#020617_100%)]" />
        <div className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full bg-fut-primary/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-28 w-[520px] h-[520px] rounded-full bg-blue-500/10 blur-3xl" />

        <div className="relative container mx-auto max-w-screen-xl w-full pt-24 md:pt-32">
          <div className="mb-8 max-w-4xl">
            <p className="text-fut-primary font-bold uppercase tracking-[.22em] mb-4">
              {t.heroEyebrow}
            </p>
            <h1 className="text-4xl md:text-6xl font-display font-black leading-tight mb-5 text-white">
              {t.heroTitle}
            </h1>
            <p className="text-lg md:text-xl text-fut-text-light/90 leading-relaxed max-w-3xl">
              {t.heroText}
            </p>
          </div>

          <div className="relative rounded-[3rem] bg-white/[.055] shadow-2xl shadow-fut-primary/10 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#001A33]/96 via-[#06111f]/93 to-black/96" />

            <div className="relative z-10 p-5 md:p-8 lg:p-10 grid xl:grid-cols-[1.05fr_.95fr] gap-8 items-center items-center">
              <div className="flex flex-col justify-between gap-6">
                <div className="rounded-[2.35rem] bg-black/22 backdrop-blur-xl p-6 md:p-8">
                  <div className="flex flex-wrap items-center gap-3 mb-5">
                    <span className="rounded-full bg-fut-primary text-fut-bg-dark px-4 py-2 text-xs font-black uppercase tracking-[.18em]">
                      San Jose Obrero LLC
                    </span>
                    <span className="rounded-full text-fut-primary px-4 py-2 text-xs font-bold uppercase tracking-[.18em]">
                      {t.heroEyebrow}
                    </span>
                  </div>

                  <h2 className="text-3xl md:text-5xl font-display font-black leading-tight mb-4 text-white">
                    {t.mainCardTitle}
                  </h2>
                  <p className="text-fut-text-light/90 text-lg leading-relaxed mb-6">
                    {t.mainCardText}
                  </p>

                  <div className="contact-visual-image-block relative mb-6 h-72 md:h-80 overflow-hidden rounded-[2.75rem] bg-white/[.04]">
                    <img
                      src="/images/businessman-2.webp"
                      alt="San Jose Obrero LLC orientación empresarial"
                      className="absolute inset-0 w-full h-full object-cover object-[center_18%] opacity-95"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#001A33]/60 via-[#001A33]/15 to-transparent" />
                    <div className="absolute bottom-5 left-5 right-5">
                      <p className="text-fut-primary font-black uppercase tracking-[.16em] text-xs">
                        Claridad · Orden · Siguiente paso
                      </p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-3">
                    {t.pillars.map((pillar) => (
                      <div
                        key={pillar.label}
                        className="rounded-2xl bg-white/[.045] p-4"
                      >
                        <p className="text-fut-primary font-black mb-2">
                          {pillar.label}
                        </p>
                        <p className="text-fut-text-light/80 text-sm leading-relaxed">
                          {pillar.text}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-[2.35rem] bg-fut-primary text-fut-bg-dark p-6 md:p-7 flex flex-col md:flex-row md:items-center justify-between gap-5">
                  <div>
                    <p className="text-2xl font-black">{t.resultTitle}</p>
                    <p className="font-semibold opacity-90 mt-1">
                      {t.resultText}
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                    <a
                      href="#lead-form"
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-fut-bg-dark text-white px-5 py-3 font-black"
                    >
                      {t.primaryCta}
                    </a>
                    <a
                      href="#booking"
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white/80 text-fut-bg-dark px-5 py-3 font-black"
                    >
                      {t.secondaryCta}
                    </a>
                  </div>
                </div>
              </div>

              <div
                id="lead-form"
                className="rounded-[2.35rem] border border-fut-primary/25 bg-white/[.055] backdrop-blur-xl p-5 md:p-7 shadow-xl shadow-fut-primary/10 max-w-lg w-full mx-auto self-center"
              >
                <div className="mb-5">
                  <h3 className="text-2xl md:text-3xl font-display font-black text-fut-primary">
                    {t.formTitle}
                  </h3>
                  <p className="text-fut-text-light/75 text-sm md:text-base mt-2">
                    {t.formText}
                  </p>
                </div>

                <form
                  name="lead-capture"
                  method="POST"
                  data-netlify="true"
                  netlify-honeypot="bot-field"
                  action="/contact"
                  className="space-y-3"
                >
                  <input type="hidden" name="form-name" value="lead-capture" />
                  <p className="hidden">
                    <label>
                      Don’t fill this out: <input name="bot-field" />
                    </label>
                  </p>

                  <div className="grid md:grid-cols-2 gap-3">
                    <input
                      name="name"
                      required
                      placeholder={t.fields.name}
                      className="w-full rounded-2xl bg-white text-slate-900 px-4 py-3"
                    />
                    <input
                      name="phone"
                      required
                      placeholder={t.fields.phone}
                      className="w-full rounded-2xl bg-white text-slate-900 px-4 py-3"
                    />
                  </div>

                  <input
                    name="email"
                    type="email"
                    placeholder={t.fields.email}
                    className="w-full rounded-2xl bg-white text-slate-900 px-4 py-3"
                  />

                  <select
                    name="service"
                    required
                    className="w-full rounded-2xl bg-white text-slate-900 px-4 py-3"
                  >
                    <option value="">{t.fields.service}</option>
                    {t.serviceOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>

                  <textarea
                    name="message"
                    placeholder={t.fields.message}
                    className="w-full rounded-2xl bg-white text-slate-900 px-4 py-3 min-h-[110px]"
                  />

                  
                  <label className="flex items-start gap-3 rounded-2xl border border-fut-primary/25 bg-white/[.06] p-4 text-sm text-fut-text-light/85">
                    <input
                      type="checkbox"
                      name="policy_acceptance"
                      value="accepted"
                      required
                      className="mt-1 h-4 w-4 shrink-0 accent-fut-primary"
                    />
                    <span>
                      {isSpanish ? (
                        <>
                          Acepto la{" "}
                          <a href="/policies" className="text-fut-primary font-bold underline">
                            Política de Privacidad y los Términos / Políticas Operativas
                          </a>{" "}
                          de San Jose Obrero LLC.
                        </>
                      ) : (
                        <>
                          I accept the{" "}
                          <a href="/policies" className="text-fut-primary font-bold underline">
                            Privacy Policy and Terms / Operating Policies
                          </a>{" "}
                          of San Jose Obrero LLC.
                        </>
                      )}
                    </span>
                  </label>

                  <button
                    type="submit"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-fut-primary text-fut-bg-dark px-6 py-4 font-black text-lg hover:bg-fut-secondary transition-all duration-300"
                  >
                    {t.submit}
                    <ArrowIcon />
                  </button>
                </form>

                <div
                  id="booking"
                  className="mt-5 rounded-2xl bg-black/18 p-5"
                >
                  <div className="flex items-start gap-3 mb-4">
                    <MiniCheck />
                    <div>
                      <h3 className="text-lg font-black text-fut-primary">
                        {t.bookingTitle}
                      </h3>
                      <p className="text-fut-text-light/75 text-sm mt-1">
                        {t.bookingText}
                      </p>
                    </div>
                  </div>

                  <a
                    href={CALENDAR_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-24 rounded-2xl bg-[#06111f] flex items-center justify-center text-center px-5 hover:bg-[#001A33] transition"
                  >
                    <span className="inline-flex items-center justify-center gap-2 text-fut-primary font-black">
                      {t.widgetLabel}
                      <ArrowIcon />
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile fixed CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-50 grid grid-cols-2 md:hidden border-t border-fut-primary/30">
        <a
          href="tel:+18884536026"
          className="bg-white text-fut-bg-dark py-4 text-center font-black"
        >
          {t.callLabel}
        </a>
        <a
          href="https://wa.me/19495687938"
          className="bg-fut-primary text-fut-bg-dark py-4 text-center font-black"
        >
          {t.whatsappLabel}
        </a>
      </div>
    </main>
  );
};

export default Contact;
